import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";import { useTranslation } from "react-i18next";
import LanguageToggle from "@/components/LanguageToggle";
import QuickActionsMenu from "@/components/QuickActionsMenu";
import JaredAssistant from "@/components/JaredAssistant";
import JaredOnboarding, { shouldShowJaredOnboarding } from "@/components/JaredOnboarding";
import useSingleTab from "@/hooks/useSingleTab";
import {
  House, FolderOpen, ClipboardText, ClockCounterClockwise, ShieldCheck, ShieldStar,
  ListChecks, FileText, ChartBar, ChartLineUp, Package, ArrowsClockwise, GridFour, Eye,
  Users as UsersIcon, SignOut, Clock, CaretRight, Heart, Leaf,
  GraduationCap, Medal, Stethoscope, PersonArmsSpread, Drop, Lightning, Recycle, Scales, Gear, ShieldWarning, BookBookmark, Pill, Truck, Lock, Palette,
  CalendarBlank, Buildings, Fire, Warning, MapTrifold, FlowArrow, Lightbulb,
  List, X, Image as ImageIcon, CreditCard, Robot, IdentificationCard, WaveTriangle, CurrencyDollar as Money, Target
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import InstallAppButton from "@/components/InstallAppButton";
import NotificationBell from "@/components/NotificationBell";
import GamificationBadge from "@/components/GamificationBadge";
import PlantSwitcher from "@/components/PlantSwitcher";
import OfflineIndicator from "@/components/OfflineIndicator";
import CatalogoDatalists from "@/components/CatalogoDatalists";
import { useEffect, useState, Suspense } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pantalla "principal" (tablero) de cada módulo con hijos. Marcar la casilla del
// módulo padre concede acceso ÚNICAMENTE a esta pantalla, NO a todas las sub-pantallas.
// Cada sub-pantalla restante es independiente y requiere su propia casilla.
const MAIN_SCREEN = {
  seguridad: "seguridad.dashboard",
  salud: "salud.dashboard",
  ambiente: "ambiente.dashboard",
  epp: "epp.dashboard",
  capacitacion_dc3: "capacitacion_dc3.listado",
};

// NOM list for sidebar
const NOMS = [
  { num: "NOM-001-STPS", title: "Edificios, locales e instalaciones" },
  { num: "NOM-002-STPS", title: "Prevención y protección contra incendios" },
  { num: "NOM-004-STPS", title: "Sistemas de protección en maquinaria" },
  { num: "NOM-005-STPS", title: "Sustancias químicas peligrosas" },
  { num: "NOM-006-STPS", title: "Manejo y almacenamiento de materiales" },
  { num: "NOM-009-STPS", title: "Trabajos en altura" },
  { num: "NOM-010-STPS", title: "Contaminantes del ambiente laboral" },
  { num: "NOM-011-STPS", title: "Condiciones de seguridad e higiene — Ruido" },
  { num: "NOM-015-STPS", title: "Condiciones térmicas" },
  { num: "NOM-017-STPS", title: "Equipo de protección personal", hasEpp: true },
  { num: "NOM-019-STPS", title: "Comisiones de seguridad e higiene" },
  { num: "NOM-020-STPS", title: "Recipientes a presión y calderas" },
  { num: "NOM-022-STPS", title: "Electricidad estática" },
  { num: "NOM-025-STPS", title: "Iluminación" },
  { num: "NOM-027-STPS", title: "Soldadura y corte" },
  { num: "NOM-030-STPS", title: "Servicios preventivos de seguridad y salud" },
  { num: "NOM-033-STPS", title: "Trabajos en espacios confinados" },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, adminPortal, brandLogo } = useAuth();
  const tabBlocked = useSingleTab(!!user);
  const { t } = useTranslation();
  const [unread, setUnread] = useState(0);
  const [showJaredOnboarding, setShowJaredOnboarding] = useState(false);
  useEffect(() => {
    if (user) {
      const id = setTimeout(() => setShowJaredOnboarding(shouldShowJaredOnboarding()), 900);
      return () => clearTimeout(id);
    }
  }, [user]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [allowedMenu, setAllowedMenu] = useState(null); // null = loading, then Set
  const [open, setOpen] = useState({
    ejecutivo: true, seguridad: true, salud: false, ambiente: false, config: false,
    capacitacion: false, proveedores: false, normas: false, "nom-017": false,
  });

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/epp/notifications`, { params: { unread_only: true } })
      .then(({ data }) => setUnread(data.length))
      .catch(() => {});
  }, [user, location.pathname]);

  // Load menu visibility for current user
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/menu-access/me`, { withCredentials: true })
      .then(({ data }) => setAllowedMenu(new Set(data.allowed || [])))
      .catch(() => setAllowedMenu(new Set())); // empty = nothing allowed except handled below
  }, [user]);

  // When the user has EPP access (which lives nested under Seguridad → NOM-017),
  // auto-expand those wrappers so the EPP screens are visible right away.
  useEffect(() => {
    if (!allowedMenu) return;
    const hasEpp = allowedMenu.has("epp") || [...allowedMenu].some(k => k.startsWith("epp."));
    if (hasEpp) setOpen(s => ({ ...s, seguridad: true, normas: true, "nom-017": true }));
  }, [allowedMenu]);

  // Admin console = logged via /system-access as Super Admin → show ONLY Configuración
  const adminConsole = user?.is_super && adminPortal;

  // Helper: can the current user see this menu key?
  // Visibility is driven ONLY by the per-user checkbox config (menu-access/me).
  // Super Admin always sees everything (backend returns all keys for is_super).
  const canSee = (key) => {
    if (adminConsole) return false; // admin console hides all operational modules
    if (!allowedMenu) return true; // while loading, show everything to avoid flicker
    return allowedMenu.has(key);
  };

  // Section header is visible if the parent module OR ANY of its child screens
  // is allowed. This lets a sub-screen show even when the parent box is unchecked.
  const sectionVisible = (...prefixes) => {
    if (adminConsole) return false;
    if (!allowedMenu) return true;
    for (const p of prefixes) {
      if (allowedMenu.has(p)) return true;
      for (const k of allowedMenu) {
        if (k.startsWith(p + ".")) return true;
      }
    }
    return false;
  };

  // Leaf (sub-screen) visible ONLY if its own checkbox is selected. Cada casilla
  // es INDEPENDIENTE: marcar el módulo padre NO muestra todas las pantallas; el
  // padre solo concede su pantalla principal/tablero (MAIN_SCREEN). El resto de
  // pantallas requieren su propia casilla.
  const leafVisible = (parentKey, key) => {
    if (adminConsole) return false;
    if (!allowedMenu) return true;
    if (!key) return true;
    if (allowedMenu.has(key)) return true;
    if (parentKey && allowedMenu.has(parentKey) && MAIN_SCREEN[parentKey] === key) return true;
    return false;
  };

  // Close mobile menu on navigation
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Auto-open Normas + NOM-017 when user is inside EPP routes
  useEffect(() => {
    if (location.pathname.startsWith("/seguridad/epp")) {
      setOpen(s => ({ ...s, seguridad: true, normas: true, "nom-017": true }));
    }
  }, [location.pathname]);

  // Admin console: keep Configuración expanded so options are visible immediately
  useEffect(() => {
    if (adminConsole) setOpen(s => ({ ...s, config: true }));
  }, [adminConsole]);

  const toggle = (k) => setOpen(s => ({ ...s, [k]: !s[k] }));

  const seguridadItems = [
    { name: t('sidebar.safetyDashboard'), path: "/seguridad", icon: ChartBar, key: "seguridad.dashboard" },
    { name: t('sidebar.unsafeActsConditions'), path: "/seguridad/actos-condiciones", icon: Warning, key: "seguridad.actos_condiciones" },
    { name: `↳ Flujo EHS`, path: "/ehs-flow", icon: FlowArrow, key: "seguridad.ehs_flow" },
    { name: t('sidebar.newAssessment'), path: "/seguridad/analysis/new", icon: ClipboardText, key: "seguridad.analisis_riesgo" },
    { name: `↳ ${t('sidebar.history')}`, path: "/seguridad/history", icon: ClockCounterClockwise, key: "seguridad.history" },
    { name: t('sidebar.a3Reports'), path: "/seguridad/a3", icon: FileText, key: "seguridad.a3" },
    { name: `↳ Flujo A3`, path: "/flujo-a3", icon: FlowArrow, key: "seguridad.a3_flow" },
    { name: t('sidebar.workPermits'), path: "/seguridad/permisos-trabajo", icon: Fire, key: "seguridad.permisos" },
    { name: `↳ AST`, path: "/seguridad/ast", icon: ClipboardText, key: "seguridad.ast" },
    { name: `↳ Instrucción Visual`, path: "/seguridad/instruccion-visual", icon: ImageIcon, key: "seguridad.instruccion_visual" },
    { name: t('sidebar.nomInspections'), path: "/seguridad/nom-inspections", icon: ListChecks, key: "seguridad.nom_inspections" },
    { name: `↳ ${t('sidebar.capaActions')}`, path: "/seguridad/actions", icon: ListChecks, key: "seguridad.actions" },
    { name: `↳ ${t('sidebar.autoCapa')}`, path: "/seguridad/nom-inspections/capas", icon: ListChecks, key: "seguridad.auto_capa" },
  ];

  const eppItems = [
    { name: t('sidebar.eppDashboard'), path: "/seguridad/epp", icon: ChartBar, key: "epp.dashboard" },
    { name: t('sidebar.eppInventory'), path: "/seguridad/epp/inventory", icon: Package, key: "epp.inventory" },
    { name: t('sidebar.eppMovements'), path: "/seguridad/epp/movements", icon: ArrowsClockwise, key: "epp.movements" },
    { name: t('sidebar.eppRequests'), path: "/seguridad/epp/requests", icon: ClipboardText, badge: unread || null, key: "epp.requests" },
    { name: t('sidebar.eppAssignments'), path: "/seguridad/epp/assignments", icon: Clock, key: "epp.assignments" },
    { name: t('sidebar.eppMatrix'), path: "/seguridad/epp/matrix", icon: GridFour, key: "epp.matrix" },
  ];

  const saludItems = [
    { name: t('sidebar.healthDashboard'), path: "/salud", icon: ChartBar, key: "salud.dashboard" },
    { name: t('sidebar.disabilityControl'), path: "/salud/incapacidades", icon: Heart, key: "salud.incapacidades" },
    { name: t('sidebar.medicalConsultations'), path: "/salud/consultas", icon: Stethoscope, key: "salud.consultas" },
    { name: t('sidebar.medications'), path: "/salud/medicamentos", icon: Pill, key: "salud.medicamentos" },
    { name: t('sidebar.medicalSurveillance'), path: "/salud/vigilancia", icon: Stethoscope, key: "salud.vigilancia" },
    { name: "Expediente Médico", path: "/salud/expediente", icon: IdentificationCard, key: "salud.expediente" },
    { name: "Vigilancia por Exposición", path: "/salud/vigilancia-exposicion", icon: WaveTriangle, key: "salud.vigilancia_exposicion" },
    { name: t('sidebar.ergonomics'), path: "/salud/ergonomia", icon: PersonArmsSpread, key: "salud.ergonomia" },
    { name: t('sidebar.psychosocial'), path: "/salud/psicosocial", icon: ShieldWarning, key: "salud.psicosocial" },
  ];

  const ambienteItems = [
    { name: t('sidebar.environmentDashboard'), path: "/ambiente", icon: ChartBar, key: "ambiente.dashboard" },
    { name: "Aspectos & Impactos", path: "/ambiente/aspectos-impactos", icon: Leaf, key: "ambiente.aspectos_impactos" },
    { name: "↳ Inventario de residuos", path: "/ambiente/aspectos-impactos/inventario-residuos", icon: Recycle, key: "ambiente.inventario_residuos" },
    { name: t('sidebar.envSummary', { defaultValue: 'Resumen Anual' }), path: "/ambiente/resumen", icon: ChartLineUp, key: "ambiente.resumen" },
    { name: t('sidebar.waste'), path: "/ambiente/residuos", icon: Recycle, key: "ambiente.residuos" },
    { name: t('sidebar.wasteLog'), path: "/ambiente/bitacora-residuos", icon: ClipboardText, key: "ambiente.bitacora_residuos" },
    { name: `↳ ${t('sidebar.exitPasses')}`, path: "/ambiente/residuos/pases-salida", icon: Truck, key: "ambiente.pases_salida" },
    { name: t('sidebar.waterConsumption'), path: "/ambiente/consumos", icon: Drop, key: "ambiente.consumos" },
    { name: t('sidebar.energyConsumption'), path: "/ambiente/energia", icon: Lightning, key: "ambiente.energia" },
    { name: t('sidebar.legalCompliance'), path: "/ambiente/cumplimiento", icon: Scales, key: "ambiente.cumplimiento" },
    { name: `↳ Flujo Legal`, path: "/flujo-legal", icon: FlowArrow, key: "ambiente.legal_flow" },
  ];

  const configItems = [];
  if (user?.is_super && adminPortal) {
    configItems.push({ name: t('sidebar.companies'), path: "/admin/empresas", icon: Buildings });
    configItems.push({ name: t('sidebar.users'), path: "/admin/users", icon: UsersIcon });
    configItems.push({ name: t('sidebar.accessControl'), path: "/admin/accesos", icon: Lock });
    configItems.push({ name: "Asistente Jared", path: "/admin/jared", icon: Robot });
    configItems.push({ name: "Diagnóstico permisos", path: "/admin/diagnostico-permisos", icon: ShieldWarning });
    configItems.push({ name: t('sidebar.auditLog'), path: "/admin/bitacora", icon: ClockCounterClockwise });
  }
  // Gerente de empresa (no Super Admin): personaliza su empresa y gestiona sus usuarios.
  if (!user?.is_super && user?.role === "gerente") {
    configItems.push({ name: t('sidebar.customizeCompany', 'Personalizar empresa'), path: "/personalizar", icon: Palette });
    configItems.push({ name: "Asistente Jared", path: "/admin/jared", icon: Robot });
    configItems.push({ name: "Diagnóstico permisos", path: "/admin/diagnostico-permisos", icon: ShieldWarning });
    configItems.push({ name: t('sidebar.users'), path: "/admin/users", icon: UsersIcon });
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const roleLabel = {
    gerente: t('role.gerente'), almacen: t('role.almacen'),
    supervisor: t('role.supervisor'), supervisor_ehs: t('role.supervisor_ehs'),
    lider_area: 'Líder de área', coordinador_almacen: 'Coordinador Almacén',
    coordinador_ehs: 'Coordinador EHS', especialista_ehs: 'Especialista EHS',
    logistica_ehs: 'Logística EHS', servicio_medico: 'Servicio Médico', rh: 'Recursos Humanos (RH)', proveedor: 'Proveedor', usuario: t('role.usuario')
  }[user?.role] || user?.role;

  const isActive = (path) => location.pathname === path;
  const startsWith = (p) => location.pathname.startsWith(p);

  const NavLink = ({ item, indent = 0 }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <Link
        to={item.path}
        data-testid={`nav-${item.path.replace(/\//g, "-")}`}
        className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
          active ? "bg-[var(--brand,#E31E24)] text-white font-semibold" : "text-slate-300 hover:bg-zinc-900 hover:text-white"
        }`}
        style={{ paddingLeft: `${12 + indent * 8}px` }}
      >
        {Icon && <Icon size={16} weight={active ? "duotone" : "regular"} />}
        <span className="flex-1">{item.name}</span>
        {item.badge ? (
          <span className="text-[10px] bg-[var(--brand,#E31E24)] text-white px-1.5 py-0.5 rounded-full font-bold">{item.badge}</span>
        ) : null}
      </Link>
    );
  };

  const SectionHeader = ({ id, label, icon: Icon, accent }) => (
    <button
      onClick={() => toggle(id)}
      data-testid={`nav-section-${id}`}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-[11px] uppercase tracking-widest font-bold text-slate-400 hover:text-white hover:bg-zinc-900 transition-colors"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <Icon size={14} weight="duotone" style={{ color: accent }} />
      <span className="flex-1 text-left">{label}</span>
      <CaretRight size={11} className={`transition-transform ${open[id] ? "rotate-90" : ""}`} />
    </button>
  );

  if (tabBlocked) {
    return (
      <div data-testid="single-tab-blocked" className="flex h-screen w-screen items-center justify-center bg-[#0a0a0a] text-white p-6">
        <div className="max-w-md text-center space-y-5">
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "rgba(227,30,36,0.15)" }}>
            <Lock size={30} weight="duotone" style={{ color: "var(--brand,#E31E24)" }} />
          </div>
          <h1 className="text-2xl font-bold">Sesión abierta en otra pestaña</h1>
          <p className="text-sm text-slate-300 leading-relaxed">
            Abriste la aplicación en una pestaña nueva, por lo que esta sesión se pausó aquí.
            Continúa en la pestaña más reciente o retoma el control en esta.
          </p>
          <button
            data-testid="single-tab-use-here"
            onClick={() => { try { localStorage.removeItem("lgmg_tab_lock"); } catch { /* noop */ } window.location.reload(); }}
            className="px-5 py-2.5 rounded-md text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            style={{ background: "var(--brand,#E31E24)" }}
          >
            Usar en esta pestaña
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#F8FAFC] relative">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-black text-white flex items-center justify-between px-4 py-3 border-b-4 border-[var(--brand,#E31E24)]">
        <Link to="/" className="flex items-center gap-2">
          <img src={adminConsole ? "/assets/logo-admin.png" : (brandLogo || "/assets/logo.png")} alt="HSE" className="w-12 h-12 object-contain" />
          <span className="font-black tracking-tight text-lg">HSE</span>
        </Link>
        <div className="flex items-center gap-1">
          <LanguageToggle variant="dark" />
          <NotificationBell variant="dark" />
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Abrir menú"
            className="w-10 h-10 flex items-center justify-center rounded-sm hover:bg-zinc-900"
          >
            <List size={24} />
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Sidebar (desktop: static; mobile: drawer) */}
      <aside
        className={`fixed lg:static top-0 left-0 z-50 w-72 h-screen bg-black text-white flex flex-col transform transition-transform duration-200 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        style={adminConsole ? undefined : { backgroundColor: "var(--menu-bg, #0a0a0a)", color: "var(--brand-text, #ffffff)" }}
        data-testid="sidebar"
      >
        {/* Brand + mobile close */}
        <div className="p-5 border-b-4 border-[var(--brand,#E31E24)] flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-home">
            <img src={adminConsole ? "/assets/logo-admin.png" : (brandLogo || "/assets/logo.png")} alt="HSE" className="w-16 h-16 object-contain" />
            <div>
              <h1 className="text-2xl font-black tracking-tight">HSE</h1>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest">Sistema Integral</p>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            {/* Idioma + campana: solo escritorio; en móvil ya están en la barra superior */}
            <div className="hidden lg:flex items-center gap-1">
              <LanguageToggle variant="dark" />
              <NotificationBell variant="dark" />
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close"
              aria-label="Cerrar menú"
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-sm hover:bg-zinc-900"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <PlantSwitcher />
        <OfflineIndicator />

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {/* EHS Safety Dashboard */}
          {canSee("ehs") && (
          <Link
            to="/ehs"
            data-testid="nav-ehs"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-bold transition-colors ${
              isActive("/ehs") ? "bg-[var(--brand,#E31E24)] text-white" : "text-white hover:bg-zinc-900"
            }`}
          >
            <ShieldStar size={18} weight={isActive("/ehs") ? "duotone" : "regular"} />
            {t('sidebar.boardEHS')}
          </Link>
          )}

          {/* DIR · Control de Gastos (arriba de Ejecutivo) */}
          {canSee("dir") && (
          <Link
            to="/dir"
            data-testid="nav-dir"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-bold transition-colors ${
              isActive("/dir") ? "bg-[var(--brand,#E31E24)] text-white" : "text-white hover:bg-zinc-900"
            }`}
          >
            <Money size={18} weight={isActive("/dir") ? "duotone" : "regular"} />
            DIR · Control de Gastos
          </Link>
          )}

          {/* Executive Dashboard + BBS (submenú desplegable) */}
          {sectionVisible("ejecutivo") && (
          <div className="mt-3">
            <SectionHeader id="ejecutivo" label={t('sidebar.executive')} icon={ChartBar} accent="var(--brand,#E31E24)" />
            {open.ejecutivo && (
              <div className="mt-1 ml-2 space-y-0.5 border-l border-zinc-800 pl-2">
                <Link
                  to="/"
                  data-testid="nav-ejecutivo"
                  className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                    isActive("/") ? "bg-[var(--brand,#E31E24)] text-white font-semibold" : "text-slate-300 hover:bg-zinc-900 hover:text-white"
                  }`}
                >
                  <ChartBar size={16} weight={isActive("/") ? "duotone" : "regular"} />
                  <span className="flex-1">{t('sidebar.executiveDashboard', { defaultValue: 'Dashboard Ejecutivo' })}</span>
                </Link>
                {leafVisible("ejecutivo", "ejecutivo.bbs") && <Link
                  to="/bbs"
                  data-testid="nav-bbs"
                  className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                    startsWith("/bbs") ? "bg-[var(--brand,#E31E24)] text-white font-semibold" : "text-slate-300 hover:bg-zinc-900 hover:text-white"
                  }`}
                >
                  <Eye size={16} weight={startsWith("/bbs") ? "duotone" : "regular"} />
                  <span className="flex-1">BBS</span>
                </Link>}
              </div>
            )}
          </div>
          )}

          {/* Política de Seguridad (institucional) — oculta en el portal system-access */}
          {!adminConsole && (
          <Link
            to="/politica-seguridad"
            data-testid="nav-politica-seguridad"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-bold transition-colors ${
              isActive("/politica-seguridad") ? "bg-[var(--brand,#E31E24)] text-white" : "text-white hover:bg-zinc-900"
            }`}
          >
            <ShieldCheck size={18} weight={isActive("/politica-seguridad") ? "duotone" : "regular"} />
            {t('sidebar.safetyPolicy')}
          </Link>
          )}

          {/* Programa de Seguimiento EHS (Gantt) */}
          {!adminConsole && canSee("programa_seguimiento") && (
          <Link
            to="/programa-seguimiento"
            data-testid="nav-programa-seguimiento"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-bold transition-colors ${
              isActive("/programa-seguimiento") ? "bg-[var(--brand,#E31E24)] text-white" : "text-white hover:bg-zinc-900"
            }`}
          >
            <CalendarBlank size={18} weight={isActive("/programa-seguimiento") ? "duotone" : "regular"} />
            {t('sidebar.followUpProgram')}
          </Link>
          )}

          {/* SEGURIDAD */}
          {sectionVisible("seguridad", "epp", "lecciones") && (
          <div className="mt-3">
            <SectionHeader id="seguridad" label={t('sidebar.safety')} icon={ShieldCheck} accent="var(--brand,#E31E24)" />
            {open.seguridad && (
              <div className="mt-1 ml-2 space-y-0.5 border-l border-zinc-800 pl-2">
                {seguridadItems.filter(item => leafVisible("seguridad", item.key)).map(item => <NavLink key={item.path} item={item} />)}

                {/* Procedimientos (NOM) */}
                {leafVisible("seguridad", "seguridad.procedimientos") && (
                  <Link
                    to="/seguridad/procedimientos"
                    data-testid="nav-seguridad-procedimientos"
                    className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                      startsWith("/seguridad/procedimientos")
                        ? "bg-[var(--brand,#E31E24)] text-white font-semibold" : "text-slate-300 hover:bg-zinc-900 hover:text-white"
                    }`}
                  >
                    <FileText size={16} weight={startsWith("/seguridad/procedimientos") ? "duotone" : "regular"} />
                    <span className="flex-1">{t('sidebar.procedures')}</span>
                  </Link>
                )}

                {/* Normas STPS — expandable with NOMs nested */}
                {(leafVisible("seguridad", "seguridad.normas") || sectionVisible("epp")) && (
                <>
                <button
                  onClick={() => toggle("normas")}
                  data-testid="nav-section-normas"
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                    startsWith("/seguridad/normas") || startsWith("/seguridad/epp")
                      ? "text-white font-semibold" : "text-slate-300 hover:bg-zinc-900 hover:text-white"
                  }`}
                >
                  <BookBookmark size={16} weight={open.normas ? "duotone" : "regular"} />
                  <span className="flex-1 text-left">{t('sidebar.normsSTPS')}</span>
                  <CaretRight size={11} className={`transition-transform ${open.normas ? "rotate-90" : ""}`} />
                </button>

                {open.normas && (
                  <div className="mt-1 ml-2 space-y-0.5 border-l border-zinc-800/70 pl-2">
                    {NOMS.map(nom => {
                      const nomPath = `/seguridad/normas/${nom.num.toLowerCase()}`;
                      const nomActive = location.pathname === nomPath ||
                                        (nom.hasEpp && startsWith("/seguridad/epp"));
                      const showNomLinks = leafVisible("seguridad", "seguridad.normas");
                      if (nom.hasEpp && !sectionVisible("epp") && !showNomLinks) return null;
                      if (!nom.hasEpp && !showNomLinks) return null;

                      if (nom.hasEpp) {
                        // NOM-017 with nested EPP submenu
                        return (
                          <div key={nom.num}>
                            <button
                              onClick={() => toggle("nom-017")}
                              data-testid="nav-section-nom-017"
                              className={`w-full flex items-center gap-2 px-2 py-2 rounded-sm text-[12px] transition-colors ${
                                nomActive ? "bg-zinc-900 text-white font-bold" : "text-slate-300 hover:bg-zinc-900 hover:text-white"
                              }`}
                            >
                              <span className="font-mono text-[10px] bg-[var(--brand,#E31E24)] text-white px-1.5 py-0.5 rounded-sm flex-shrink-0">
                                {nom.num.replace("-STPS", "")}
                              </span>
                              <span className="flex-1 text-left text-[11px]">{nom.title}</span>
                              <CaretRight size={10} className={`transition-transform ${open["nom-017"] ? "rotate-90" : ""}`} />
                            </button>
                            {open["nom-017"] && (
                              <div className="mt-1 ml-2 pl-2 border-l-2 border-[var(--brand,#E31E24)]/40 space-y-0.5">
                                <p className="px-2 py-1 text-[9px] uppercase tracking-widest text-[var(--brand,#E31E24)]/80 font-bold">
                                  {t('sidebar.eppManagement')}
                                </p>
                                {eppItems.filter(item => leafVisible("epp", item.key)).map(item => <NavLink key={item.path} item={item} />)}
                              </div>
                            )}
                          </div>
                        );
                      }

                      return (
                        <Link
                          key={nom.num}
                          to={nomPath}
                          data-testid={`nav-nom-${nom.num.toLowerCase()}`}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-[11px] transition-colors ${
                            isActive(nomPath) ? "bg-[var(--brand,#E31E24)] text-white font-semibold" : "text-slate-400 hover:bg-zinc-900 hover:text-white"
                          }`}
                        >
                          <span className="font-mono text-[10px] bg-zinc-800 px-1.5 py-0.5 rounded-sm flex-shrink-0">
                            {nom.num.replace("-STPS", "")}
                          </span>
                          <span className="flex-1 truncate">{nom.title}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
                </>
                )}

                {/* Inspecciones de Planta — recorrido de seguridad por categorías */}
                {leafVisible("seguridad", "seguridad.inspecciones_planta") && (
                  <Link
                    to="/seguridad/inspecciones-planta"
                    data-testid="nav-seguridad-inspecciones-planta"
                    className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                      startsWith("/seguridad/inspecciones-planta")
                        ? "bg-[var(--brand,#E31E24)] text-white font-semibold" : "text-slate-300 hover:bg-zinc-900 hover:text-white"
                    }`}
                  >
                    <ListChecks size={16} weight={startsWith("/seguridad/inspecciones-planta") ? "duotone" : "regular"} />
                    <span className="flex-1">{t('sidebar.plantInspections')}</span>
                  </Link>
                )}

                {/* Inventario de Seguridad — por categorías, con foto, GPS y estado */}
                {leafVisible("seguridad", "seguridad.inventario") && (
                  <Link
                    to="/seguridad/inventario"
                    data-testid="nav-seguridad-inventario"
                    className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                      startsWith("/seguridad/inventario")
                        ? "bg-[var(--brand,#E31E24)] text-white font-semibold" : "text-slate-300 hover:bg-zinc-900 hover:text-white"
                    }`}
                  >
                    <Package size={16} weight={startsWith("/seguridad/inventario") ? "duotone" : "regular"} />
                    <span className="flex-1">{t('sidebar.inventory', { defaultValue: 'Inventario de Seguridad' })}</span>
                  </Link>
                )}

                {/* Lecciones Aprendidas — último submenú de Seguridad */}
                {canSee("lecciones") && (
                  <Link
                    to="/lecciones"
                    data-testid="nav-lecciones"
                    className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                      startsWith("/lecciones")
                        ? "bg-[var(--brand,#E31E24)] text-white font-semibold" : "text-slate-300 hover:bg-zinc-900 hover:text-white"
                    }`}
                  >
                    <Lightbulb size={16} weight={startsWith("/lecciones") ? "duotone" : "regular"} />
                    <span className="flex-1">{t('sidebar.lessons')}</span>
                  </Link>
                )}
              </div>
            )}
          </div>
          )}

          {/* SALUD */}
          {sectionVisible("salud") && (
          <div className="mt-3">
            <SectionHeader id="salud" label={t('sidebar.health')} icon={Heart} accent="#0EA5E9" />
            {open.salud && (
              <div className="mt-1 ml-2 space-y-0.5 border-l border-zinc-800 pl-2">
                {saludItems.filter(item => leafVisible("salud", item.key)).map(item => <NavLink key={item.path} item={item} />)}
              </div>
            )}
          </div>
          )}

          {/* AMBIENTE */}
          {sectionVisible("ambiente") && (
          <div className="mt-3">
            <SectionHeader id="ambiente" label={t('sidebar.environment')} icon={Leaf} accent="#16A34A" />
            {open.ambiente && (
              <div className="mt-1 ml-2 space-y-0.5 border-l border-zinc-800 pl-2">
                {ambienteItems.filter(item => leafVisible("ambiente", item.key)).map(item => <NavLink key={item.path} item={item} />)}
              </div>
            )}
          </div>
          )}

          {/* CAPACITACIÓN — top-level collapsible below Medio Ambiente */}
          {sectionVisible("capacitacion_dc3") && (
            <div className="mt-3">
              <button
                onClick={() => toggle("capacitacion")}
                data-testid="nav-section-capacitacion"
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-bold transition-colors ${
                  startsWith("/salud/capacitaciones") ? "bg-zinc-900 text-white" : "text-white hover:bg-zinc-900"
                }`}
              >
                <GraduationCap size={18} weight={open.capacitacion ? "duotone" : "regular"} />
                <span className="flex-1 text-left">{t('sidebar.training')}</span>
                <CaretRight size={12} className={`transition-transform ${open.capacitacion ? "rotate-90" : ""}`} />
              </button>
              {open.capacitacion && (
                <div className="mt-1 ml-2 space-y-0.5 border-l border-zinc-800 pl-2">
                  {leafVisible("capacitacion_dc3", "capacitacion_dc3.dashboard") && (
                  <Link
                    to="/salud/capacitaciones/dashboard"
                    data-testid="nav-capacitacion-dashboard"
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                      isActive("/salud/capacitaciones/dashboard")
                        ? "bg-[var(--brand,#E31E24)] text-white font-bold"
                        : "text-slate-200 hover:bg-zinc-900"
                    }`}
                  >
                    <ChartBar size={14} weight={isActive("/salud/capacitaciones/dashboard") ? "duotone" : "regular"} />
                    Dashboard
                  </Link>
                  )}
                  {leafVisible("capacitacion_dc3", "capacitacion_dc3.dnc") && (
                  <Link
                    to="/salud/capacitaciones/dnc"
                    data-testid="nav-capacitacion-dnc"
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                      isActive("/salud/capacitaciones/dnc")
                        ? "bg-[var(--brand,#E31E24)] text-white font-bold"
                        : "text-slate-200 hover:bg-zinc-900"
                    }`}
                  >
                    <Target size={14} weight={isActive("/salud/capacitaciones/dnc") ? "duotone" : "regular"} />
                    DNC
                  </Link>
                  )}
                  {leafVisible("capacitacion_dc3", "capacitacion_dc3.elearning") && (
                  <Link
                    to="/salud/capacitaciones/elearning"
                    data-testid="nav-capacitacion-elearning"
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                      isActive("/salud/capacitaciones/elearning")
                        ? "bg-[var(--brand,#E31E24)] text-white font-bold"
                        : "text-slate-200 hover:bg-zinc-900"
                    }`}
                  >
                    <GraduationCap size={14} weight={isActive("/salud/capacitaciones/elearning") ? "duotone" : "regular"} />
                    E-Learning
                  </Link>
                  )}
                  {leafVisible("capacitacion_dc3", "capacitacion_dc3.listado") && (
                  <Link
                    to="/salud/capacitaciones"
                    data-testid="nav-capacitacion-listado"
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                      isActive("/salud/capacitaciones")
                        ? "bg-[var(--brand,#E31E24)] text-white font-bold"
                        : "text-slate-200 hover:bg-zinc-900"
                    }`}
                  >
                    <ListChecks size={14} weight={isActive("/salud/capacitaciones") ? "duotone" : "regular"} />
                    {t('sidebar.trainingList')}
                  </Link>
                  )}
                  {leafVisible("capacitacion_dc3", "capacitacion_dc3.plan_anual") && (
                  <Link
                    to="/salud/capacitaciones/plan-anual"
                    data-testid="nav-capacitacion-plan-anual"
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                      isActive("/salud/capacitaciones/plan-anual")
                        ? "bg-[var(--brand,#E31E24)] text-white font-bold"
                        : "text-slate-200 hover:bg-zinc-900"
                    }`}
                  >
                    <CalendarBlank size={14} weight={isActive("/salud/capacitaciones/plan-anual") ? "duotone" : "regular"} />
                    {t('sidebar.trainingPlan')}
                  </Link>
                  )}
                  {leafVisible("capacitacion_dc3", "capacitacion_dc3.asistencias") && (
                  <Link
                    to="/salud/capacitaciones/asistencias"
                    data-testid="nav-capacitacion-asistencias"
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                      isActive("/salud/capacitaciones/asistencias")
                        ? "bg-[var(--brand,#E31E24)] text-white font-bold"
                        : "text-slate-200 hover:bg-zinc-900"
                    }`}
                  >
                    <ClipboardText size={14} weight={isActive("/salud/capacitaciones/asistencias") ? "duotone" : "regular"} />
                    {t('sidebar.trainingAttendance')}
                  </Link>
                  )}
                  {leafVisible("capacitacion_dc3", "capacitacion_dc3.generar") && (
                  <Link
                    to="/salud/capacitaciones/generar-dc3"
                    data-testid="nav-generar-dc3"
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                      isActive("/salud/capacitaciones/generar-dc3")
                        ? "bg-[var(--brand,#E31E24)] text-white font-bold"
                        : "text-slate-200 hover:bg-zinc-900"
                    }`}
                  >
                    <FileText size={14} weight={isActive("/salud/capacitaciones/generar-dc3") ? "duotone" : "regular"} />
                    {t('sidebar.generateDC3')}
                  </Link>
                  )}
                  {(user?.role === "gerente" || user?.is_super) && (
                  <Link
                    to="/admin/areas"
                    data-testid="nav-registro-areas"
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                      isActive("/admin/areas")
                        ? "bg-[var(--brand,#E31E24)] text-white font-bold"
                        : "text-slate-200 hover:bg-zinc-900"
                    }`}
                  >
                    <MapTrifold size={14} weight={isActive("/admin/areas") ? "duotone" : "regular"} />
                    {t('sidebar.areaRegistry')}
                  </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {/* PROVEEDORES — top-level collapsible */}
          {canSee("proveedores") && (
          <div className="mt-3">
            <button
              onClick={() => toggle("proveedores")}
              data-testid="nav-section-proveedores"
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-bold transition-colors ${
                startsWith("/proveedores") ? "bg-zinc-900 text-white" : "text-white hover:bg-zinc-900"
              }`}
            >
              <Buildings size={18} weight={open.proveedores ? "duotone" : "regular"} />
              <span className="flex-1 text-left">{t('sidebar.suppliers')}</span>
              <CaretRight size={12} className={`transition-transform ${open.proveedores ? "rotate-90" : ""}`} />
            </button>
            {open.proveedores && (
              <div className="mt-1 ml-2 space-y-0.5 border-l border-zinc-800 pl-2">
                {[
                  { name: t('sidebar.supDirectory'), path: "/proveedores", icon: Buildings, exact: true },
                  { name: t('sidebar.supLegalDocs'), path: "/proveedores/documentacion", icon: FileText },
                  { name: t('sidebar.supAuthorizedPersonnel'), path: "/proveedores/personal", icon: UsersIcon },
                  { name: t('sidebar.supVehicles'), path: "/proveedores/vehiculos", icon: Truck },
                  { name: t('sidebar.supDailyCheckin'), path: "/proveedores/checkin", icon: ShieldCheck },
                ].map(it => {
                  const Icon = it.icon;
                  const active = it.exact
                    ? location.pathname === it.path
                    : isActive(it.path);
                  return (
                    <Link key={it.path} to={it.path}
                          data-testid={`nav-prov-${it.path.split("/").pop()}`}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                            active ? "bg-[var(--brand,#E31E24)] text-white font-bold" : "text-slate-200 hover:bg-zinc-900"
                          }`}>
                      <Icon size={14} weight={active ? "duotone" : "regular"} />
                      {it.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
          )}


          {/* PROYECTOS — top-level link below Medio Ambiente */}
          {canSee("proyectos") && (
          <Link
            to="/config/projects"
            data-testid="nav-proyectos"
            className={`mt-3 flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-semibold transition-colors ${
              isActive("/config/projects") ? "bg-[var(--brand,#E31E24)] text-white" : "text-slate-200 hover:bg-zinc-900"
            }`}
          >
            <FolderOpen size={18} weight={isActive("/config/projects") ? "duotone" : "regular"} />
            {t('sidebar.projects')}
          </Link>
          )}

          {/* AUDITORÍA — top-level link below Proyectos */}
          {canSee("auditoria") && (
          <Link
            to="/auditoria"
            data-testid="nav-auditoria"
            className={`mt-1 flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-semibold transition-colors ${
              startsWith("/auditoria") ? "bg-[var(--brand,#E31E24)] text-white" : "text-slate-200 hover:bg-zinc-900"
            }`}
          >
            <ClipboardText size={18} weight={startsWith("/auditoria") ? "duotone" : "regular"} />
            {t('sidebar.audit')}
          </Link>
          )}

          {/* CONFIG */}
          {configItems.length > 0 && (
            <div className="mt-3">
              <SectionHeader id="config" label={t('sidebar.config')} icon={Gear} accent="#64748B" />
              {open.config && (
                <div className="mt-1 ml-2 space-y-0.5 border-l border-zinc-800 pl-2">
                  {configItems.map(item => <NavLink key={item.path} item={item} />)}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-zinc-800 space-y-2">
          {!adminConsole && canSee("ranking") && <GamificationBadge />}
          {!adminConsole && <InstallAppButton />}
          <button
            type="button"
            onClick={() => navigate("/mi-capacitacion")}
            data-testid="user-profile-btn"
            title="Ver mi capacitación"
            className="w-full flex items-center gap-3 px-2 py-2 rounded-sm bg-zinc-950 hover:bg-zinc-900 hover:ring-1 hover:ring-[var(--brand,#E31E24)] transition text-left">
            <div className="w-9 h-9 rounded-sm bg-[var(--brand,#E31E24)] flex items-center justify-center font-bold text-sm">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">{roleLabel}</p>
            </div>
            {!adminConsole && <BadgeCount />}
            <GraduationCap size={16} className="text-slate-500 shrink-0" />
          </button>
          {user?.is_super && <LoginLogoToggle />}
          {user?.is_super && <SignupToggle />}
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-zinc-800 text-slate-300 hover:bg-[var(--brand,#E31E24)] hover:border-[var(--brand,#E31E24)] hover:text-white text-xs uppercase tracking-wider font-bold transition-colors"
          >
            <SignOut size={14} /> {t('common.signOut')}
          </button>
        </div>
      </aside>

      <CatalogoDatalists />
      <main className="flex-1 overflow-auto pt-14 lg:pt-0 pb-16">
        <ErrorBoundary key={location.pathname}>
          <Suspense fallback={
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="content-loading">
              <div className="h-8 w-8 rounded-full border-4 border-slate-200 border-t-[var(--brand,#E31E24)] animate-spin" />
            </div>
          }>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>

      {/* Menú rápido móvil (oculto mientras el cajón está abierto) */}
      {!mobileOpen && !adminConsole && <QuickActionsMenu />}

      {/* Asistente conversacional Jared (global) */}
      <JaredAssistant />
      {showJaredOnboarding && (
        <JaredOnboarding onClose={() => setShowJaredOnboarding(false)} />
      )}
    </div>
  );
}


// Toggle Super Admin: habilita/deshabilita el logo de la pantalla de login.
function LoginLogoToggle() {
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/system/login-branding`)
      .then(r => setEnabled(r.data?.login_logo_enabled !== false))
      .catch(() => {});
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    try {
      await axios.patch(`${API}/system/login-branding`, { login_logo_enabled: next });
      setEnabled(next);
      localStorage.setItem("login_logo_enabled", String(next));
      toast.success(next ? "Logo de login habilitado" : "Logo de login deshabilitado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar");
    } finally { setSaving(false); }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving}
      data-testid="login-logo-toggle"
      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-zinc-800 text-slate-300 hover:bg-zinc-800 text-xs transition-colors"
      title="Muestra u oculta el logo en la pantalla de inicio de sesión"
    >
      <span className="flex items-center gap-2">
        <ImageIcon size={14} /> Logo de login
      </span>
      <span className={`inline-flex items-center h-5 w-9 rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-zinc-600"}`}>
        <span className={`h-4 w-4 bg-white rounded-full transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}


// Toggle Super Admin: habilita/deshabilita el botón "Suscripción" (auto-registro) del login.
function SignupToggle() {
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/system/login-branding`)
      .then(r => setEnabled(r.data?.signup_enabled !== false))
      .catch(() => {});
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    try {
      await axios.patch(`${API}/system/login-branding`, { signup_enabled: next });
      setEnabled(next);
      localStorage.setItem("signup_enabled", String(next));
      toast.success(next ? "Suscripción (auto-registro) habilitada" : "Suscripción (auto-registro) deshabilitada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar");
    } finally { setSaving(false); }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving}
      data-testid="signup-toggle"
      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-zinc-800 text-slate-300 hover:bg-zinc-800 text-xs transition-colors mb-2"
      title="Muestra u oculta el botón de Suscripción (auto-registro) en la pantalla de login"
    >
      <span className="flex items-center gap-2">
        <CreditCard size={14} /> Botón Suscripción
      </span>
      <span className={`inline-flex items-center h-5 w-9 rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-zinc-600"}`}>
        <span className={`h-4 w-4 bg-white rounded-full transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}

function BadgeCount() {
  const [n, setN] = useState(null);
  const loc = useLocation();
  useEffect(() => {
    axios.get(`${API}/capacitacion/elearning/mis-insignias`).then(r => setN(r.data.total)).catch(() => setN(null));
  }, [loc.pathname]);
  if (!n) return null;
  return (
    <span className="inline-flex items-center gap-0.5 shrink-0 rounded-full bg-amber-500/15 text-amber-400 text-[11px] font-bold px-1.5 py-0.5"
          title={`${n} insignia(s) de logro`} data-testid="user-badge-count">
      <Medal size={12} weight="fill" />{n}
    </span>
  );
}
